import { inject } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { EventApi } from '../../apis/event/event-api';
import { EventEntity } from '../../apis/event/event-entity';
import { EventAccessApi } from '../../apis/event-access/event-access-api';
import { UserContext } from '../../contexts/user-context';
import { ProjectModelService } from '../../services/project-model-service';
import { I18N } from '@aurelia/i18n';
@inject(IRouter, EventApi, EventAccessApi, UserContext, ProjectModelService, I18N)
export class MyEvents {
    public events: EventEntity[] = [];
    public invitesCount: number = 0;
    public isBusy: boolean = false;
    public showAdvancedTools: boolean = false;
    public copyCode: string = '';
    public copyErrorKey: string | null = null;
    public isCopying: boolean = false;
    public importCode: string = '';
    public importPin: string = '';
    public importErrorKey: string | null = null;
    public importSuccess: boolean = false;
    public isImporting: boolean = false;
    public isOrphaning: boolean = false;

    constructor(
        private readonly _router: IRouter,
        private readonly _eventApi: EventApi,
        private readonly _eventAccessApi: EventAccessApi,
        private readonly _userContext: UserContext,
        private readonly _projectModelService: ProjectModelService,
        private readonly _i18n: I18N,
    ) {}

    public async loading(): Promise<void> {
        this.isBusy = true;
        try {
            const result = await this._eventApi.getMine();
            this.events = result.events;
            this.invitesCount = result.invitesCount;
        } catch (error) {
            const apiError = error as { statusCode?: number };
            if (apiError.statusCode === 401) {
                void this._router.load('login?returnUrl=my-events');
            }
        } finally {
            this.isBusy = false;
        }
    }

    public viewInvites(): void {
        void this._router.load('invites');
    }

    public get isLoggedIn(): boolean {
        return this._userContext.isLoggedIn;
    }

    public get copyCodeValid(): boolean {
        return this.copyCode.length === 6;
    }

    public get importCodeValid(): boolean {
        return this.importCode.length === 6;
    }

    public get areActionsBusy(): boolean {
        return this.isBusy || this.isCopying || this.isImporting || this.isOrphaning;
    }

    public createNew(): void {
        const project = this._projectModelService.createNewProject();
        void this._router.load(`create/event-summary/${project.event.id}`);
    }

    public toggleAdvancedTools(): void {
        this.showAdvancedTools = !this.showAdvancedTools;
    }

    public editEvent(event: EventEntity): void {
        void this._router.load(`create/event-summary/${event.id}`);
    }

    public shareEvent(event: EventEntity): void {
        void this._router.load(`create/share-event/${event.id}`);
    }

    public viewEvent(event: EventEntity): void {
        if (!event?.code) { return; }
        const url = `${window.location.origin}/${event.code}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    public async copyEvent(event: EventEntity): Promise<void> {
        this.isBusy = true;
        try {
            await this._copyFromCode(event.code);
        } catch (err) {
            const error = err as { statusCode?: number };
            if (error.statusCode === 401 || error.statusCode === 403) {
                void this._router.load('login?returnUrl=my-events');
            } else {
                this.copyErrorKey = 'copy_schedule_failed';
                this.showAdvancedTools = true;
            }
        } finally {
            this.isBusy = false;
        }
    }

    public async claimEvent(): Promise<void> {
        if (!this.importCode) { return; }

        this.isImporting = true;
        this.importErrorKey = null;
        this.importSuccess = false;

        try {
            const result = await this._eventApi.claim(this.importCode, this.importPin || undefined);
            if (result.statusCode >= 200 && result.statusCode < 300) {
                this.importSuccess = true;
                const mine = await this._eventApi.getMine();
                this.events = mine.events;
                this.invitesCount = mine.invitesCount;
            } else if (result.statusCode === 409) {
                this.importErrorKey = 'import_event_owned_by_other';
            } else if (result.statusCode === 401) {
                this.importErrorKey = 'import_event_invalid_pin';
            } else if (result.statusCode === 404) {
                this.importErrorKey = 'import_event_not_found';
            } else {
                this.importErrorKey = 'import_event_not_found';
            }
        } catch {
            this.importErrorKey = 'import_event_not_found';
        } finally {
            this.isImporting = false;
        }
    }

    public async removeSharedEvent(event: EventEntity): Promise<void> {
        this.isBusy = true;
        try {
            await this._eventAccessApi.removeSelf(event.id);
            this.events = this.events.filter(e => e.id !== event.id);
        } finally {
            this.isBusy = false;
        }
    }

    public goToRegistration(event: EventEntity): void {
        void this._router.load(`registration/${event.code}`);
    }

    public async orphanEvent(event: EventEntity): Promise<void> {
        const confirmation = this._i18n.tr('ui:orphan_event_confirmation');
        if (!window.confirm(confirmation)) { return; }

        this.isOrphaning = true;
        try {
            await this._eventApi.orphan(event.id);
            this.events = this.events.filter(e => e.id !== event.id);
        } finally {
            this.isOrphaning = false;
        }
    }

    private async _copyFromCode(code: string): Promise<void> {
        await this._eventApi.copy(code);
        // Reload so the copy lands where the server's ordering (start date, then creation time) puts it.
        const mine = await this._eventApi.getMine();
        this.events = mine.events;
        this.invitesCount = mine.invitesCount;
    }
}
